import type { PlayerLocation } from "../networking/SignalingTransport";

const LOCATION_TIMEOUT_MS = 12_000;
const REVERSE_GEOCODE_URL = "https://api.bigdatacloud.net/data/reverse-geocode-client";

export const requestNearbyLocation = (): Promise<PlayerLocation> => {
  if (!navigator.geolocation) return Promise.reject(new Error("Location is not available in this browser."));
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => void resolveLocation(coords).then(resolve),
      (error) => reject(new Error(locationError(error))),
      { enableHighAccuracy: true, maximumAge: 60_000, timeout: LOCATION_TIMEOUT_MS },
    );
  });
};

const resolveLocation = async (coords: GeolocationCoordinates): Promise<PlayerLocation> => {
  const location = {
    latitude: roundCoordinate(coords.latitude),
    longitude: roundCoordinate(coords.longitude),
  };
  try {
    const url = new URL(REVERSE_GEOCODE_URL);
    url.searchParams.set("latitude", String(location.latitude));
    url.searchParams.set("longitude", String(location.longitude));
    url.searchParams.set("localityLanguage", "en");
    const response = await fetch(url);
    if (!response.ok) return location;
    const result = await response.json() as ReverseGeocodeResult;
    return {
      ...location,
      city: result.city || result.locality || undefined,
      country: result.countryCode || undefined,
      region: result.principalSubdivision || undefined,
    };
  } catch {
    return location;
  }
};

const roundCoordinate = (value: number): number => Math.round(value * 100_000) / 100_000;

interface ReverseGeocodeResult {
  city?: string;
  locality?: string;
  countryCode?: string;
  principalSubdivision?: string;
}

const locationError = (error: GeolocationPositionError): string => {
  if (error.code === error.PERMISSION_DENIED) return "Location permission was declined. Rooms will stay sorted by newest.";
  if (error.code === error.TIMEOUT) return "Location took too long. Please retry.";
  return "Your location could not be determined.";
};
